import { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Target, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import MathKeyboard from "../components/MathKeyboard";
import MathText from "../components/MathText";
import ModeGate from "../components/ModeGate";
import MatchTools from "../components/MatchTools";
import useSubjectTopics from "../hooks/useSubjectTopics";

const SUBJECTS = ["Math","Physics","Chemistry","Biology","English","History","Geography"];
const GRADES   = ["Grade 9","Grade 10","Grade 11","Grade 12"];

export default function Practice() {
  return (
    <ModeGate mode="Practice" accent="amber">
      <PracticeRoom />
    </ModeGate>
  );
}

function PracticeRoom() {
  const nav = useNavigate();
  const [subject, setSubject] = useState("Math");
  const [grade, setGrade] = useState("Grade 10");
  const { topics, topic, setTopic } = useSubjectTopics(subject, grade, { extra: ["Mixed"] });
  const [difficulty, setDifficulty] = useState("medium");
  const [phase, setPhase] = useState("setup");
  const [session, setSession] = useState(null);
  const [q, setQ] = useState(null);
  const [answer, setAnswer] = useState("");
  const [startTs, setStartTs] = useState(0);
  const [result, setResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/practice/start", {
        subject,
        topic: topic === "Mixed" ? null : topic,
        grade,
        difficulty,
      });
      setSession(r.data.session_id);
      setPhase("playing");
      await nextQuestion();
    } catch (e) { toast.error("Failed to start"); }
    finally { setBusy(false); }
  };

  const nextQuestion = async () => {
    setResult(null); setAnswer("");
    const params = new URLSearchParams({ subject, difficulty, grade });
    if (topic && topic !== "Mixed") params.set("topic", topic);
    try {
      const r = await apiClient.get(`/questions/random?${params}`);
      setQ(r.data);
      setStartTs(Date.now());
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No ${subject} questions for ${grade}.`);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!answer.trim() || !q) return;
    const elapsed = Date.now() - startTs;
    const r = await apiClient.post("/practice/answer", { session_id: session, question_id: q.id, answer, elapsed_ms: elapsed });
    setResult(r.data);
    if (r.data.correct) toast.success("Correct!", { duration: 1500 });
    else toast.error("Incorrect answer", { duration: 2000 });
  };

  const endSession = async () => {
    if (!session) { setPhase("setup"); return; }
    const r = await apiClient.post(`/practice/end/${session}`);
    setSummary(r.data);
    setPhase("end");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <button onClick={()=>nav("/")} className="font-mono text-xs tracking-[0.3em] text-soft hover:text-white flex items-center gap-2 mb-6" data-testid="back-home"><ArrowLeft size={14}/>BACK TO MENU</button>
      <div className="font-mono text-[10px] tracking-[0.4em] text-amber uppercase mb-2">PRACTICE MODE</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl mb-8">SOLO VS. <span className="text-amber">AI</span></h1>

      <AnimatePresence mode="wait">
      {phase === "setup" && (
        <motion.div key="s" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SetupField label="SUBJECT" testid="practice-subject" value={subject} onChange={setSubject} options={SUBJECTS}/>
          <SetupField label="TOPIC"   testid="practice-topic"   value={topic}   onChange={setTopic}   options={topics}/>
          <SetupField label="GRADE"   testid="practice-grade"   value={grade}   onChange={setGrade}   options={GRADES}/>
          <SetupField label="AI DIFFICULTY" testid="practice-difficulty" value={difficulty} onChange={setDifficulty} options={[
            { value: "easy", label: "Easy" }, { value: "medium", label: "Medium" }, { value: "hard", label: "Hard" }
          ]}/>
          <button className="sm-btn-primary px-6 py-4 md:col-span-2" onClick={start} disabled={busy} data-testid="practice-start-btn">
            <Target className="inline mr-2" size={16}/>START SESSION
          </button>
        </motion.div>
      )}
      {phase === "playing" && q && (
        <motion.div key="p" initial={{opacity:0}} animate={{opacity:1}} className="sm-card p-8">
          <div className="flex justify-between items-center">
            <div className="font-mono text-[10px] tracking-[0.3em] text-blue">{q.subject} · {q.topic} · {q.grade}</div>
            <button onClick={endSession} className="sm-btn-outline px-3 py-1.5 text-xs" data-testid="end-session-btn">END SESSION</button>
          </div>
          <div className="font-heading font-bold text-2xl sm:text-3xl mt-4 question-text"><MathText>{q.question}</MathText></div>
          {!result ? (
            <>
              <form onSubmit={submit} className="mt-6 flex gap-3">
                <input autoFocus className="sm-input flex-1" value={answer} onChange={e=>setAnswer(e.target.value)} placeholder="Your answer..." data-testid="practice-answer-input"/>
                <button type="submit" className="sm-btn-primary px-6" data-testid="practice-submit">SUBMIT</button>
              </form>
              <MathKeyboard onInsert={(t)=>setAnswer(a=>a+t)}/>
            </>
          ) : (
            <div className="mt-6">
              <div className={`p-4 rounded-xl border-l-2 ${result.correct?'border-[#34D399] bg-[#34D399]/5':'border-[#F43F5E] bg-[#F43F5E]/5'}`}>
                <div className="font-heading font-bold text-lg">{result.correct?"CORRECT":"INCORRECT"}</div>
                <div className="text-sm text-soft mt-1">Answer: <span className="font-mono"><MathText>{result.correct_answer}</MathText></span></div>
                {result.explanation && <div className="text-sm text-faint mt-2"><MathText>{result.explanation}</MathText></div>}
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={nextQuestion} className="sm-btn-primary px-5 py-2" data-testid="practice-next-btn">NEXT</button>
              </div>
            </div>
          )}
        </motion.div>
      )}
      {phase === "end" && summary && (
        <motion.div key="e" initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} className="sm-card p-10">
          <div className="font-mono text-xs tracking-[0.4em] text-blue">SESSION SUMMARY</div>
          <div className="font-heading font-black text-4xl mt-3">{summary.accuracy}% <span className="text-faint text-2xl">ACCURACY</span></div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="QUESTIONS" value={summary.total}/>
            <Stat label="CORRECT" value={summary.correct} accent="text-win"/>
            <Stat label="AVG SPEED" value={`${(summary.avg_response_ms/1000).toFixed(1)}s`}/>
            <Stat label="STRONGEST" value={summary.strongest_topic || "-"}/>
          </div>
          <div className="mt-6 text-soft">Weakest topic: <span className="text-purple">{summary.weakest_topic || "-"}</span></div>
          <button className="sm-btn-primary px-6 py-3 mt-8" onClick={()=>{ setPhase("setup"); setSummary(null);}} data-testid="practice-restart">NEW SESSION</button>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Whiteboard + calculator — only while actually answering questions */}
      {phase === "playing" && <MatchTools />}
    </div>
  );
}

function SetupField({ label, value, onChange, options, testid }) {
  const opts = options.map(o => typeof o === "string" ? { value: o, label: o } : o);
  return (
    <label className="sm-card p-5">
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue">{label}</div>
      <select className="sm-input mt-3" value={value} onChange={e=>onChange(e.target.value)} data-testid={testid}>
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Stat({ label, value, accent="text-blue" }) {
  return (
    <div className="sm-card p-4">
      <div className="font-mono text-[10px] tracking-[0.3em] text-faint">{label}</div>
      <div className={`font-heading font-black text-xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
